import React from "react";
import { Box, Avatar, Typography, Button, Tooltip, Chip } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import CloseIcon from "@mui/icons-material/Close";
import RoleChips from "./RoleChips";
import { roleMatchesFrameworkFilter } from "../../../../utils/staffRoleFrameworkFilter";

const AdminTableCell = ({
  row,
  column,
  onRemoveAdmin,
  onRemoveRole,
  peopleOpsNameData,
  complianceNameData,
  infosecNameData,
  consentMgrNameData,
  dpoNameData,
  subscribedFrameworkKeys,
  roleFrameworkKeysByDescription,
}) => {
  if (column?.field === "name") {
    return (
      <Box
        display="flex"
        alignItems="center"
        sx={{
          transition: "transform 0.2s ease",
          "&:hover": {
            transform: "translateX(4px)",
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: blue[600],
            mr: 2,
            width: 42,
            height: 42,
            fontSize: "1rem",
            fontWeight: 600,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            border: "2px solid white",
          }}
        >
          {row?.name?.charAt(0)}
        </Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              lineHeight: 1.4,
              color: "text.primary",
              mb: 0.5,
            }}
          >
            {row?.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: grey[600],
              fontSize: "0.875rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            {row?.email}
          </Typography>
        </Box>
      </Box>
    );
  } else if (column?.field === "staff_role") {
    const hasRole = row?.staff_role && row?.staff_role !== "No Role";
    return (
      <Chip
        label={row?.staff_role}
        size="small"
        sx={{
          bgcolor: hasRole ? "rgba(26,86,219,0.08)" : "background.paper",
          color: hasRole ? "primary.main" : "text.secondary",
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          borderRadius: 1,
          px: 1.5,
          boxShadow: "none",
          "& .MuiChip-label": {
            px: 0.2,
          },
        }}
      />
    );
  } else if (column?.field === "roles") {
    const filteredRoles = row?.roles?.filter(
      (role) =>
        role?.role_description &&
        role?.role_name?.toLowerCase() !== "admin" &&
        roleMatchesFrameworkFilter(
          roleFrameworkKeysByDescription?.[role?.role_description],
          subscribedFrameworkKeys
        )
    );
    if (!filteredRoles || filteredRoles?.length === 0) {
      return (
        <Box sx={{ textAlign: "left", width: "100%" }}>
          <Typography
            variant="body2"
            sx={{
              color: grey[500],
              fontStyle: "italic",
              fontSize: "0.875rem",
              textAlign: "left",
            }}
          >
            No admin roles
          </Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: "left", width: "100%" }}>
        <RoleChips
          roles={row?.roles}
          onRemoveRole={onRemoveRole}
          peopleOpsNameData={peopleOpsNameData}
          complianceNameData={complianceNameData}
          infosecNameData={infosecNameData}
          consentMgrNameData={consentMgrNameData}
          dpoNameData={dpoNameData}
          subscribedFrameworkKeys={subscribedFrameworkKeys}
          roleFrameworkKeysByDescription={roleFrameworkKeysByDescription}
        />
      </Box>
    );
  } else if (column?.field === "actions") {
    return (
      <Box display="flex" gap={1}>
        <Tooltip title="Remove admin access" placement="left" arrow>
          <Button
            size="small"
            variant="outlined"
            color="error"
            sx={{
              minWidth: "auto",
              px: 2,
              py: 0.5,
              borderRadius: "6px",
              fontSize: "0.8125rem",
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              },
              transition: "all 0.2s ease",
            }}
            onClick={() => onRemoveAdmin(row)}
            startIcon={<CloseIcon sx={{ fontSize: "0.875rem" }} />}
          >
            Remove
          </Button>
        </Tooltip>
      </Box>
    );
  }
  return row?.[column?.field];
};

export default AdminTableCell;
